import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type PageId = 'proxies' | 'sms' | 'permanent' | 'wallet' | 'global';

const PAGE_IDS: PageId[] = ['proxies', 'sms', 'permanent', 'wallet', 'global'];

const FLAGS: { code: string; name: string }[] = [
  { code: 'us', name: 'United States' },
  { code: 'gb', name: 'United Kingdom' },
  { code: 'de', name: 'Germany' },
  { code: 'nl', name: 'Netherlands' },
  { code: 'fr', name: 'France' },
  { code: 'ca', name: 'Canada' },
  { code: 'au', name: 'Australia' },
  { code: 'jp', name: 'Japan' },
  { code: 'kr', name: 'South Korea' },
  { code: 'sg', name: 'Singapore' },
  { code: 'ae', name: 'UAE' },
  { code: 'tr', name: 'Turkey' },
  { code: 'sa', name: 'Saudi Arabia' },
  { code: 'se', name: 'Sweden' },
  { code: 'ch', name: 'Switzerland' },
  { code: 'es', name: 'Spain' },
  { code: 'it', name: 'Italy' },
  { code: 'pl', name: 'Poland' },
  { code: 'br', name: 'Brazil' },
  { code: 'in', name: 'India' },
];

function Flag({ code, name }: { code: string; name: string }) {
  return (
    <div className="dx-flag" title={name}>
      <img src={`https://flagcdn.com/w80/${code}.png`} alt={name} loading="lazy" width={40} height={30} />
      <span>{code.toUpperCase()}</span>
    </div>
  );
}

function PageProxies() {
  const { t } = useTranslation();
  const rows = [
    { loc: 'DE • Frankfurt', type: t('home.liveMock.proxyType.residential'), ms: '16ms' },
    { loc: 'US • New York', type: t('home.liveMock.proxyType.dedicated'), ms: '28ms' },
    { loc: 'NL • Amsterdam', type: t('home.liveMock.proxyType.residential'), ms: '23ms' },
    { loc: 'SG • Singapore', type: t('home.liveMock.proxyType.shared'), ms: '31ms' },
  ];
  return (
    <div className="dx-page__panel">
      <div className="dx-page__list">
        {rows.map((r, i) => (
          <div key={r.loc} className={`dx-page__row ${i === 1 ? 'is-hot' : ''}`} style={{ animationDelay: `${i * 50}ms` }}>
            <div>
              <strong>{r.loc}</strong>
              <span>{r.type}</span>
            </div>
            <em>{r.ms}</em>
            <b className="is-on">{t('home.liveMock.statusLive')}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

function PageSms() {
  const { t } = useTranslation();
  return (
    <div className="dx-page__panel dx-page__sms">
      <div className="dx-phone">
        <div className="dx-phone__notch" />
        <div className="dx-phone__msg">
          <small>{t('home.liveMock.incomingSms')}</small>
          <strong>WhatsApp: 482-913</strong>
        </div>
        <div className="dx-phone__meta">
          <span>+49 • DE</span>
          <span>{t('home.liveMock.justNow')}</span>
        </div>
      </div>
      <div className="dx-page__sidefacts">
        <div>
          <span>{t('home.liveMock.country')}</span>
          <strong>{t('home.liveMock.germany')}</strong>
        </div>
        <div className="is-hot">
          <span>{t('home.liveMock.service')}</span>
          <strong>WhatsApp</strong>
        </div>
        <div>
          <span>{t('home.liveMock.status')}</span>
          <strong className="ok">{t('home.liveMock.received')}</strong>
        </div>
      </div>
    </div>
  );
}

function PagePermanent() {
  const { t } = useTranslation();
  return (
    <div className="dx-page__panel">
      <div className="dx-perm">
        <div className="dx-perm__num is-hot">+44 7700 900123</div>
        <div className="dx-perm__tags">
          <span>{t('home.liveMock.permanent')}</span>
          <span>30 {t('home.liveMock.days')}</span>
          <span className="ok">{t('home.liveMock.receivingSms')}</span>
        </div>
        <div className="dx-perm__bars" aria-hidden="true">
          {[40, 65, 48, 80, 55, 90, 70, 85].map((h, i) => (
            <i key={i} style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PageWallet() {
  const { t } = useTranslation();
  return (
    <div className="dx-page__panel dx-page__wallet">
      <div className="dx-wallet-card is-hot">
        <span>{t('home.liveMock.currentBalance')}</span>
        <strong>$248.60</strong>
        <small>{t('home.liveMock.lastTopUp')}</small>
      </div>
      <div className="dx-page__sidefacts">
        <div>
          <span>{t('home.liveMock.crypto')}</span>
          <strong>USDT</strong>
        </div>
        <div>
          <span>{t('home.liveMock.status')}</span>
          <strong className="ok">{t('home.liveMock.completed')}</strong>
        </div>
        <div>
          <span>{t('home.liveMock.readyToBuy')}</span>
          <strong>{t('home.liveMock.yes')}</strong>
        </div>
      </div>
    </div>
  );
}

function PageGlobal() {
  const { t } = useTranslation();
  const loop = [...FLAGS, ...FLAGS];
  return (
    <div className="dx-page__panel dx-page__global">
      <div className="dx-global__stats">
        <div>
          <strong>100+</strong>
          <span>{t('home.liveMock.countries')}</span>
        </div>
        <div>
          <strong>99.9%</strong>
          <span>{t('home.liveMock.uptime')}</span>
        </div>
        <div>
          <strong>24/7</strong>
          <span>{t('home.liveMock.support')}</span>
        </div>
      </div>
      <div className="dx-flags-scroll" aria-hidden="true">
        <div className="dx-flags-track">
          {loop.map((f, i) => (
            <Flag key={`${f.code}-${i}`} code={f.code} name={f.name} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PageBody({ pageId }: { pageId: PageId }) {
  switch (pageId) {
    case 'proxies':
      return <PageProxies />;
    case 'sms':
      return <PageSms />;
    case 'permanent':
      return <PagePermanent />;
    case 'wallet':
      return <PageWallet />;
    case 'global':
      return <PageGlobal />;
    default:
      return null;
  }
}

export function LiveDashboardMock() {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState<'up' | 'down'>('up');
  const pageId = PAGE_IDS[index];

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return;
    const id = window.setInterval(() => {
      setDir('up');
      setIndex((i) => (i + 1) % PAGE_IDS.length);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  const go = (next: number) => {
    setDir(next > index || (index === PAGE_IDS.length - 1 && next === 0) ? 'up' : 'down');
    setIndex(next);
  };

  return (
    <div className="dx-stage">
      <div className="dx-stage__glow" aria-hidden="true" />
      <div className="dx-stage__frame">
        <div className="dx-stage__chrome">
          <span className="dx-stage__traffic" aria-hidden="true">
            <i /><i /><i />
          </span>
          <span className="dx-stage__chrome-title">{t('home.liveMock.chromeTitle')}</span>
          <span className="dx-stage__live">
            <b />
            {t('home.liveMock.live')}
          </span>
        </div>
        <div className="dx-stage__body">
          <aside className="dx-stage__rail" aria-hidden="true">
            {PAGE_IDS.map((id, i) => (
              <button
                key={id}
                type="button"
                className={`dx-stage__rail-btn ${i === index ? 'is-on' : ''}`}
                onClick={() => go(i)}
                title={t(`home.liveMock.pages.${id}.kicker`)}
              />
            ))}
          </aside>
          <div className="dx-stage__viewport">
            <div key={pageId} className={`dx-stage__page dx-stage__page--${dir}`}>
              <div className="dx-stage__head">
                <span className="dx-stage__kicker">{t(`home.liveMock.pages.${pageId}.kicker`)}</span>
                <h3>{t(`home.liveMock.pages.${pageId}.title`)}</h3>
                <p>{t(`home.liveMock.pages.${pageId}.desc`)}</p>
              </div>
              <PageBody pageId={pageId} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
